import React, { useEffect, useRef, useState } from 'react'

import cx from 'classnames'
import get from 'lodash/get'
import size from 'lodash/size'
import uniqBy from 'lodash/uniqBy'

import { useScrollToBottom } from '../../../scrollToBottom'

import styles from './seekStepProcess.module.scss'

const DefaultAssetIcon = '/images/default_asset.png'

/*
 * ---------------------------------------------------------------------------
 * Data contract (produced by the consumer repos as the socket stream arrives)
 * ---------------------------------------------------------------------------
 * Each chat that opted into the new loader carries:
 *   chat.showPlan   : boolean  -> render this component instead of the old
 *                                  "Processing your request..." placeholder
 *   chat.planSteps  : array    -> the raw stream events, appended in arrival
 *                                  order. No ordering/merging is assumed here;
 *                                  this component normalizes them. Shape per
 *                                  event mirrors the socket payload:
 *                                    { type, payload, completed, ... }
 *                                  where type is one of:
 *                                    'planning'      -> "Thinking through your question"
 *                                    'search_asset'  -> "Searching relevant insights"
 *                                    'final_answer'  -> "Crafting your response" / "Response generated"
 *                                  'follow_up' is intentionally ignored here -
 *                                  follow-up questions render their own loader.
 * ---------------------------------------------------------------------------
 */

const TYPE_TO_KEY = {
  planning: 'planning',
  search_asset: 'search',
  final_answer: 'answer'
}

const STEP_ORDER = ['planning', 'search', 'answer']

const STEP_META = {
  planning: {
    active: 'Thinking through your question',
    done: 'Thinking through your question'
  },
  search: {
    active: 'Searching relevant insights',
    done: 'Searching relevant insights'
  },
  answer: {
    active: 'Crafting your response',
    done: 'Response generated'
  }
}

const assetKey = (asset) =>
  get(asset, 'csAsset.id') || get(asset, 'id') || get(asset, 'name')

/*
 * Turn the unordered raw stream events into an ordered, de-duplicated view
 * model. Events of the same logical step are merged (the two paired
 * `search_asset` events - the "scanning" message and its `{assets}` result -
 * collapse into a single search step holding every discovered asset).
 */
export const buildStepModel = (events = []) => {
  const present = {}

  events.forEach((event) => {
    const key = TYPE_TO_KEY[get(event, 'type')]
    if (!key) return

    if (!present[key]) {
      present[key] = { key, assets: [], completedFlag: false }
    }

    if (get(event, 'completed')) {
      present[key].completedFlag = true
    }

    // Live status / reasoning text streamed in the payload:
    //  - planning & search_asset (incomplete) carry a plain string payload
    //  - final_answer carries it on payload.thinking
    const payload = get(event, 'payload')
    if (typeof payload === 'string' && payload.trim()) {
      present[key].message = payload.trim()
    }
    const thinking = get(event, 'payload.thinking')
    if (typeof thinking === 'string' && thinking.trim()) {
      present[key].message = thinking.trim()
    }

    if (key === 'search') {
      const assets = get(event, 'payload.assets')
      if (Array.isArray(assets) && assets.length > 0) {
        present[key].assets = uniqBy(
          [...present[key].assets, ...assets],
          assetKey
        )
      }
    }
  })

  const orderedKeys = STEP_ORDER.filter((key) => present[key])
  const answerDone = get(present, 'answer.completedFlag', false)

  const steps = orderedKeys.map((key, index) => {
    const laterStepStarted = index < orderedKeys.length - 1
    const done =
      present[key].completedFlag ||
      laterStepStarted ||
      (key !== 'answer' && answerDone)

    return {
      key,
      assets: present[key].assets,
      message: present[key].message,
      status: done ? 'done' : 'active',
      label: done ? STEP_META[key].done : STEP_META[key].active
    }
  })

  return {
    steps,
    allComplete: answerDone,
    searchComplete: get(present, 'search.completedFlag', false)
  }
}

const Spinner = () => <span className={styles.spinner} />

const AssetPill = ({ asset }) => {
  const name = get(asset, 'name') || get(asset, 'csAsset.name', '')
  const thumbnail =
    get(asset, 'csAsset.icon.thumbnail') ||
    get(asset, 'icon.thumbnail') ||
    DefaultAssetIcon
  const url = get(asset, 'targetUrl') || get(asset, 'csAsset.alias') || null

  const handleClick = (event) => {
    if (!url) {
      event.preventDefault()
    }
  }

  return (
    <a
      className={styles.assetPill}
      href={url || undefined}
      target='_blank'
      rel='noopener noreferrer'
      onClick={handleClick}
    >
      <img className={styles.pillThumb} src={thumbnail} alt={name} />
      <span className={styles.pillName}>{name}</span>
    </a>
  )
}

const StepRow = ({ step }) => {
  // Planning reasoning (and any non-search message) stays visible; the search
  // "Scanning..." status is transient and gives way to the asset cards.
  const showMessage =
    step.message && (step.status === 'active' || step.key !== 'search')

  return (
    <div className={styles.stepRow}>
      <div className={styles.bullet}>
        {step.status === 'active' ? <Spinner /> : <span className={styles.dot} />}
      </div>
      <div className={styles.stepContent}>
        <span className={styles.stepLabel}>{step.label}</span>
        {showMessage && (
          <span className={styles.stepMessage}>{step.message}</span>
        )}
        {size(step.assets) > 0 && (
          <div className={styles.assetPills}>
            {step.assets.map((asset) => (
              <AssetPill key={assetKey(asset)} asset={asset} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Artificial pacing so the "answer" step never flashes past instantly:
//  crafting (dummy loader) -> response generated -> collapse -> reveal answer.
const CRAFTING_MIN_MS = 1100
const GENERATED_HOLD_MS = 800
const REVEAL_DELAY_MS = 350

export const SeekStepProcess = ({ events = [], active = false, onReveal }) => {
  const scrollToBottom = useScrollToBottom()

  const { steps: rawSteps, allComplete: answerComplete, searchComplete } =
    buildStepModel(events)

  // planning + search come straight from the stream; the answer step is driven
  // by the local phase machine below so we can pace it.
  const baseSteps = rawSteps.filter((step) => step.key !== 'answer')
  const dataAnswerStep = rawSteps.find((step) => step.key === 'answer')

  // A chat restored from history mounts already finished - skip the animation.
  // Captured once at mount: after a streaming chat commits it looks identical
  // (active=false, answerComplete=true), so this must NOT be recomputed.
  const historicalRef = useRef(!active && answerComplete)
  const historical = historicalRef.current

  const [answerPhase, setAnswerPhase] = useState(() =>
    historical ? 'done' : 'idle'
  )
  const [collapsed, setCollapsed] = useState(() => historical)
  const [craftingMinElapsed, setCraftingMinElapsed] = useState(false)
  const userInteracted = useRef(false)
  const revealedRef = useRef(false)

  const fireReveal = () => {
    if (!revealedRef.current) {
      revealedRef.current = true
      if (onReveal) onReveal()
    }
  }

  // Historical chats reveal their answer immediately.
  useEffect(() => {
    if (historical) {
      fireReveal()
    }
  }, [])

  // idle -> crafting: kick off the dummy "Crafting your response" loader as
  // soon as the asset search returns (or the answer lands, if there was no
  // search step) so the reveal always progresses.
  useEffect(() => {
    if (answerPhase === 'idle' && (searchComplete || answerComplete)) {
      setAnswerPhase('crafting')
    }
  }, [answerPhase, searchComplete, answerComplete])

  // Hold the crafting loader on screen for a minimum duration.
  useEffect(() => {
    if (answerPhase === 'crafting') {
      const timer = setTimeout(() => setCraftingMinElapsed(true), CRAFTING_MIN_MS)
      return () => clearTimeout(timer)
    }
  }, [answerPhase])

  // crafting -> generated: once the answer is in and the loader has shown long
  // enough, flip the label to "Response generated".
  useEffect(() => {
    if (answerPhase === 'crafting' && answerComplete && craftingMinElapsed) {
      setAnswerPhase('generated')
    }
  }, [answerPhase, answerComplete, craftingMinElapsed])

  // generated -> done: keep "Response generated" visible briefly, then collapse.
  useEffect(() => {
    if (answerPhase === 'generated') {
      const timer = setTimeout(() => {
        if (!userInteracted.current) {
          setCollapsed(true)
        }
        setAnswerPhase('done')
      }, GENERATED_HOLD_MS)
      return () => clearTimeout(timer)
    }
  }, [answerPhase])

  // After the process collapses, reveal the response a beat later.
  useEffect(() => {
    if (answerPhase === 'done' && !historical) {
      const timer = setTimeout(fireReveal, REVEAL_DELAY_MS)
      return () => clearTimeout(timer)
    }
  }, [answerPhase])

  // Keep the conversation pinned to the latest content while steps stream in.
  useEffect(() => {
    requestAnimationFrame(() => scrollToBottom())
  }, [size(baseSteps), answerPhase, collapsed])

  const togglePill = () => {
    userInteracted.current = true
    setCollapsed((value) => !value)
  }

  // Assemble the rendered steps: base steps + the paced answer step.
  const displaySteps = [...baseSteps]
  if (answerPhase === 'crafting') {
    displaySteps.push({
      key: 'answer',
      status: 'active',
      label: STEP_META.answer.active,
      assets: []
    })
  } else if (answerPhase === 'generated' || answerPhase === 'done') {
    displaySteps.push({
      key: 'answer',
      status: 'done',
      label: STEP_META.answer.done,
      message: get(dataAnswerStep, 'message'),
      assets: []
    })
  }

  // Before any step event has streamed in, show the shimmer header alone so a
  // loader appears the instant the request fires.
  if (size(displaySteps) === 0) {
    if (!active) {
      return null
    }
    return (
      <div className={styles.stepProcess}>
        <div className={styles.shimmerHeader}>Thinking through</div>
      </div>
    )
  }

  const isDone = answerPhase === 'done'
  const bodyCollapsed = isDone && collapsed

  return (
    <div className={styles.stepProcess}>
      {!isDone && (
        <div className={styles.shimmerHeader}>Thinking through</div>
      )}
      {isDone && (
        <button
          type='button'
          className={cx(styles.pill, { [styles.expanded]: !collapsed })}
          onClick={togglePill}
        >
          {size(displaySteps)} steps completed
          <i className={cx('cleverstory-drop-down', styles.pillChevron)} />
        </button>
      )}
      <div className={cx(styles.stepsBody, { [styles.collapsed]: bodyCollapsed })}>
        {displaySteps.map((step) => (
          <StepRow key={step.key} step={step} />
        ))}
      </div>
    </div>
  )
}

export default SeekStepProcess
